import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { isEqual, get } from 'lodash/fp';

import { intersection } from 'lodash';
import { getPermissions, SCOPES, Page } from '~/api/routes/auth/user';
import { useAuthSelector } from '~/theseus/containers/Auth';
import {
  useAccountsQuery,
  usePagesQuery,
  useFbPixelQuery,
  useFundingSourceQuery,
  usePageQuery,
} from '~/theseus/hooks/queries';
import useAdBlockerDetected from '~/theseus/hooks/useAdBlockerDetected';


export const hasPageAccess = (page?: Page) => (
  page?.tasks.includes('ADVERTISE')
  && page?.tasks.includes('CREATE_CONTENT')
);

function useAuthStatus() {
  const [pageId] = usePageQuery();
  const isOwner = useAuthSelector((state) => state.context?.rgUser?.owner);
  const siteEnabled = useAuthSelector((state) => state.context.rgUser?.enabled);
  const isAdmin = useAuthSelector((state) => state.context.rgUser?.admin);
  const accessToken = useAuthSelector((state) => state.context.authToken);
  const adBlockerDetected = useAdBlockerDetected();

  const { data: pages, status: pagesQueryStatus } = usePagesQuery();

  const { data: hasAccounts, status: accountsQueryStatus } = useAccountsQuery({
    select: (accounts) => accounts.length > 0,
  });

  // Need to ensure that all of the permissions are granted and that the
  // permissions that the user has match what we ask of the user when they log
  // in
  const { data: hasAllPermissions, status: permissionsQueryStatus } = useQuery(
    ['permissions', accessToken],
    () => getPermissions(accessToken as string),
    {
      enabled: !!accessToken,
      select: (res) => {
        const permissionStatuses = res.data.map(get('status'));
        const permissions = res.data.map(get('permission'));

        const allPermissionsGranted = permissionStatuses.every(isEqual('granted'));

        // We want to ensure that the user has all of the permissions required
        // to use our app but if they have more permissions than what we ask
        // for than that is "A-Ok"
        const filteredPermissions = intersection(permissions, SCOPES);
        const rightPermissionsGiven = isEqual(filteredPermissions.sort(), SCOPES.sort());

        return allPermissionsGranted && rightPermissionsGiven;
      },
    },
  );

  const { data: hasPixel, status: pixelQueryStatus } = useFbPixelQuery({
    select: (pixel) => !!pixel,
  });

  const { data: hasValidFundingSource, status: fundingSourceQueryStatus } = useFundingSourceQuery({
    select: (fundingSource) => !!fundingSource,
  });

  const currentPage = useMemo(() => pages?.find(({ id }) => pageId === id), [pageId, pages]);

  return {
    accounts: {
      valid: hasAccounts,
      isLoading: accountsQueryStatus === 'loading',
      isFetched: accountsQueryStatus === 'success',
    },
    pages: {
      valid: pages && pages.length > 0,
      isLoading: pagesQueryStatus === 'loading',
      isFetched: pagesQueryStatus === 'success',
    },
    fundingSource: {
      valid: hasValidFundingSource,
      isLoading: fundingSourceQueryStatus === 'loading',
      isFetched: fundingSourceQueryStatus === 'success',
    },
    fbPixel: {
      valid: hasPixel,
      isLoading: pixelQueryStatus === 'loading',
      isFetched: pixelQueryStatus === 'success',
    },
    permissions: {
      valid: hasAllPermissions,
      isLoading: permissionsQueryStatus === 'loading',
      isFetched: permissionsQueryStatus === 'success',
    },
    adBlockerDisabled: !adBlockerDetected,
    isAuthorized: isOwner || isAdmin,
    siteEnabled,
    hasPageAccess: hasPageAccess(currentPage),
  };
}

export default useAuthStatus;
